"use client";

import { motion, useInView } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "../../lib/utils";

// True character-by-character typewriter.
// Reveals one character per tick (cheap re-render, no width/layout animation),
// overlaid on an invisible full-text spacer so wrapping is stable and nothing
// below the headline shifts while it types.
export const TypewriterEffect = ({
  words,
  className,
  cursorClassName,
  speed = 45,
}) => {
  const chars = useMemo(() => {
    const out = [];
    words.forEach((word, wi) => {
      word.text.split("").forEach((char) =>
        out.push({ char, className: word.className })
      );
      if (wi < words.length - 1) out.push({ char: " ", className: undefined });
    });
    return out;
  }, [words]);

  const fullText = useMemo(() => chars.map((c) => c.char).join(""), [chars]);

  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView || count >= chars.length) return undefined;
    const id = setTimeout(() => setCount((c) => c + 1), speed);
    return () => clearTimeout(id);
  }, [inView, count, chars.length, speed]);

  return (
    <div ref={ref} className={cn("relative", className)}>
      {/* spacer: reserves the final wrapped size so nothing jumps */}
      <span className="invisible" aria-hidden="true">
        {fullText}
      </span>
      {/* typed overlay */}
      <span className="absolute inset-0">
        {chars.slice(0, count).map((c, i) => (
          <span key={i} className={cn("text-brand-plum", c.className)}>
            {c.char}
          </span>
        ))}
        <span
          className={cn(
            "ml-1 inline-block h-[0.9em] w-[3px] translate-y-[0.12em] rounded-sm bg-brand-magenta",
            count < chars.length ? "" : "animate-pulse",
            cursorClassName
          )}
        />
      </span>
    </div>
  );
};

// Width-reveal variant (single line; clips if it can't fit — keep for short text only).
export const TypewriterEffectSmooth = ({ words, className, cursorClassName }) => {
  const wordsArray = words.map((word) => ({ ...word, text: word.text.split("") }));

  const renderWords = () => (
    <div>
      {wordsArray.map((word, idx) => (
        <div key={`word-${idx}`} className="inline-block">
          {word.text.map((char, index) => (
            <span key={`char-${index}`} className={cn("text-brand-plum", word.className)}>
              {char}
            </span>
          ))}
          &nbsp;
        </div>
      ))}
    </div>
  );

  return (
    <div className={cn("flex items-end space-x-1", className)}>
      <motion.div
        className="overflow-hidden pb-1"
        initial={{ width: "0%" }}
        whileInView={{ width: "fit-content" }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: "linear", delay: 0.4 }}
      >
        <div className="font-display font-semibold" style={{ whiteSpace: "nowrap" }}>
          {renderWords()}
        </div>
      </motion.div>
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, repeat: Infinity, repeatType: "reverse" }}
        className={cn("block h-8 w-[4px] rounded-sm bg-brand-magenta sm:h-10 xl:h-14", cursorClassName)}
      />
    </div>
  );
};
